import { type PDFDocumentProxy } from 'pdfjs-dist';
import {
  createContext,
  memo,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type PDFViewerControls } from '@air/react-pdfjs-viewer';
import { type RenderOutlinePageMenu } from '@air/react-pdfjs-viewer/src/components/PDFViewerOutline/components/PDFViewerOutlinePage/PDFViewerOutlinePage';
import { type PDFPagesCache } from '@air/react-pdfjs-viewer/src/utils/PDFPagesCache';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PDFOutlineContextType = {
  isOutlineOpen: boolean;
  setIsOutlineOpen: (isOpen: boolean) => void;
  currentPage: number;
  numPages: number;
  changePage: (pageNumber: number) => void;
  pdfDocument: PDFDocumentProxy | null;
  pdfPagesCacheRef: RefObject<PDFPagesCache> | null;
  renderOutlinePageMenu?: RenderOutlinePageMenu;
  updatePDFControls: (pdfControls: PDFViewerControls) => void;
  resetPDFControls: () => void;
};

const DEFAULT_VALUE: PDFOutlineContextType = {
  isOutlineOpen: false,
  setIsOutlineOpen: () => {},
  currentPage: 1,
  numPages: 0,
  changePage: () => {},
  pdfDocument: null,
  pdfPagesCacheRef: null,
  renderOutlinePageMenu: undefined,
  updatePDFControls: () => {},
  resetPDFControls: () => {},
};

const PDFOutlineContext = createContext<PDFOutlineContextType>(DEFAULT_VALUE);

export type PDFOutlineProviderProps = {
  children: ReactNode;
};

export const PDFOutlineProvider = memo(({ children }: PDFOutlineProviderProps) => {
  const [isOutlineOpen, setIsOutlineOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const pdfPagesCacheRef = useRef<RefObject<PDFPagesCache> | null>(null);
  const renderOutlinePageMenuRef = useRef<RenderOutlinePageMenu | undefined>(undefined);
  const changePageRef = useRef<((pageNumber: number) => void) | null>(null);

  const numPages = pdfDocument?.numPages ?? 0;

  const changePage = useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
    changePageRef.current?.(pageNumber);
  }, []);

  const updatePDFControls = useCallback((newPdfControls: PDFViewerControls) => {
    setIsOutlineOpen(newPdfControls.isOutlineOpen);
    setCurrentPage(newPdfControls.currentPage);
    setPdfDocument(newPdfControls.pdfDocument);
    pdfPagesCacheRef.current = newPdfControls.pdfPagesCacheRef;
    renderOutlinePageMenuRef.current = newPdfControls.renderOutlinePageMenu;
    changePageRef.current = newPdfControls.changePage;
  }, []);

  /**
   * Clears the outline/page state so a newly-opened asset never inherits the
   * previous PDF's page count or current page. This context lives at the
   * asset-modal level (above the visualizer), so it must be reset explicitly
   * when the clip changes — otherwise stale controls linger until the next PDF
   * finishes loading.
   */
  const resetPDFControls = useCallback(() => {
    setIsOutlineOpen(false);
    setCurrentPage(1);
    setPdfDocument(null);
    pdfPagesCacheRef.current = null;
    renderOutlinePageMenuRef.current = undefined;
    changePageRef.current = null;
  }, []);

  const contextValue: PDFOutlineContextType = useMemo(
    () => ({
      isOutlineOpen,
      setIsOutlineOpen,
      currentPage,
      numPages,
      changePage,
      pdfDocument,
      pdfPagesCacheRef: pdfPagesCacheRef.current,
      renderOutlinePageMenu: renderOutlinePageMenuRef.current,
      updatePDFControls,
      resetPDFControls,
    }),
    [isOutlineOpen, currentPage, numPages, changePage, pdfDocument, updatePDFControls, resetPDFControls],
  );

  return <PDFOutlineContext.Provider value={contextValue}>{children}</PDFOutlineContext.Provider>;
});

PDFOutlineProvider.displayName = 'PDFOutlineProvider';

export const usePDFOutlineContext = (): PDFOutlineContextType => {
  const context = useContext(PDFOutlineContext);

  if (context === DEFAULT_VALUE) {
    const error = 'usePDFOutlineContext must be used within a PDFOutlineProvider';

    if (isDevOrTestStage()) {
      throw new Error(error);
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
